import { Descriptions, Table, Tag, Typography } from 'antd';
import { Link, useHistory, useParams } from 'react-router-dom';
import React, { useEffect, useState } from 'react';
import {
    chemblTargetExternalLink,
    diseaseLink,
    ingredientLink,
    keggPathExternalLink,
    keggTargetExternalLink,
    recipeLink,
    uniprotDetailLink,
    uniprotTargetExternalLink,
    plantDetailLink,
} from '../hooks/TableLink';

import _ from 'lodash';
import useFetch from '../hooks/fetcher';

// import mockImg from '../img/herb/mockImg.png';
const API_SERVICE_BASE_URL = process.env.REACT_APP_API_SERVICE_BASE_URL || '';
const DATA_SERVICE_BASE_URL = process.env.REACT_APP_DATA_SERVICE_BASE_URL || '';

const { Title } = Typography;
const { Text } = Typography;
const { Item } = Descriptions;

// backend response data structure
type ipath3Type = {
    chembl_id: string;
    color: string;
    width: string;
};
type herbDetail = {
    botanical_id: string;
    botanical_name: string;
    pinyin: string;
    chinese_pharmaceutical_name: string;
    pharmaceutical_latin_name: string;
    used_part: string;
    chp_2015: string;
};

const ipath3ResponseProcessor = (r: ipath3Type[]): string[] => r.map((e) => `${e.chembl_id} ${e.color} ${e.width}`);

const tAreaDivider = '&#13;&#10;';
const uniprotTags = (uniprotList?: string) => {
    if (_.isNil(uniprotList)) {
        return null;
    }
    return uniprotList.split(',').map((item: string, key: number) => {
        return (
            <Link to={`/target/${item}`} key={key}>
                <Tag color="blue" style={{ cursor: 'pointer', margin: '3px' }}>
                    {item}
                </Tag>
            </Link>
        );
    });
};
const atcTags = (atcList?: string) => {
    if (_.isNil(atcList)) {
        return 'NA';
    }
    return atcList.split(',').map((item: string, key: number) => {
        return (
            <a
                href={`https://www.whocc.no/atc_ddd_index/?code=+${item}+&showdescription=no`}
                target="_blank"
                rel="noopener noreferrer"
                key={key}
            >
                <Tag color="blue" style={{ cursor: 'pointer', margin: '3px' }}>
                    {item}
                </Tag>
            </a>
        );
    });
};
const columnRi = [
    {
        title: 'Ingredients ID',
        key: 'ingredients_id',
        dataIndex: 'ingredients_id',
        // eslint-disable-next-line react/display-name
        render: ingredientLink,
    },
    { title: 'Ingredients Name', key: 'compound_name', dataIndex: 'compound_name' },
    { title: 'Formula', key: 'molecular_formula', dataIndex: 'molecular_formula' },
    { title: 'MW', key: 'mw', dataIndex: 'mw' },
    { title: 'XLogP', key: 'xlogp', dataIndex: 'xlogp' },
    { title: 'HBA', key: 'hba', dataIndex: 'hba' },
    { title: 'HBD', key: 'hbd', dataIndex: 'hbd' },
    { title: 'TPSA', key: 'tpsa', dataIndex: 'tpsa' },
    { title: 'Rotatable Bond', key: 'rotatable_bond', dataIndex: 'rotatable_bond' },
    { title: 'Lipinski Failure', key: 'lipinski_failure', dataIndex: 'lipinski_failure' },
    { title: 'ATC Codes', key: 'atcs', dataIndex: 'atcs', render: atcTags },
];

const columnTargets = [
    { title: 'Ingredients ID', dataIndex: 'ingredients_id', key: 'ingredients_id', render: ingredientLink },
    { title: 'Targets', dataIndex: 'protein', key: 'protein', render: uniprotDetailLink },
    { title: 'Gene', dataIndex: 'gene', key: 'gene' },
    { title: 'ChEMBL ID', dataIndex: 'chembl_id', key: 'chembl_id', render: chemblTargetExternalLink },
    { title: 'UNIPROT ID', dataIndex: 'uniprot_entry', key: 'uniprot_entry', render: uniprotTargetExternalLink },
    { title: 'KEGG ID', dataIndex: 'kegg_id', key: 'kegg_id', render: keggTargetExternalLink },
    { title: 'Pharmacological Action', dataIndex: 'pharmacological_action', key: 'pharmacological_action' },
    { title: 'Druggable', dataIndex: 'druggable', key: 'druggable' },
];
const columnDiseases = [
    {
        title: 'Disease Code',
        key: 'level4',
        dataIndex: 'level4',
        render: diseaseLink,
    },
    {
        title: 'Disease Annotation',
        key: 'level4_description',
        dataIndex: 'level4_description',
    },
    {
        title: 'Targets',
        key: 'targets',
        dataIndex: 'targets',
        width: 350,
        render: uniprotTags,
    },
];
const columnRecipes = [
    {
        title: 'Chinese Character',
        key: 'chinese_character',
        dataIndex: 'chinese_character',
        render: recipeLink,
    },
    {
        title: 'Transliteration',
        key: 'pinyin',
        dataIndex: 'pinyin',
    },
    {
        title: 'English Name',
        key: 'english_name',
        dataIndex: 'english_name',
    },
];
const columnsPathways = [
    { title: 'KEGG Pathway', dataIndex: 'pathway_id', key: 'pathway_id', render: keggPathExternalLink },
    { title: 'Pathway Name', dataIndex: 'pathway_name', key: 'pathway_name' },
    {
        title: 'Count Druggable Targets',
        dataIndex: 'count_current_druggable_targets',
        key: 'count_current_druggable_targets',
        // sorter: (a: ChineseDrugsType['related_pathways'], b: ChineseDrugsType['related_pathways']) =>
        //     a[0].count_current_druggable_targets - b[0].count_current_druggable_targets,
    },
    {
        title: 'PValue',
        dataIndex: 'p21',
        key: 'p21',
        // sorter: (a: ChineseDrugsType['related_pathways'], b: ChineseDrugsType['related_pathways']) => a.p21 - b.p21,
    },
    {
        title: 'EValue',
        dataIndex: 'e21',
        key: 'e21',
        // sorter: (a: ChineseDrugsType['related_pathways'], b: ChineseDrugsType['related_pathways']) => a.e21 - b.e21,
    },
];
type Related_ingredient = {
    ingredients_id: string;
    compound_name: string;
    molecular_formula: string;
    mw: string;
    xlogp: string;
    hba: string;
    hbd: string;
    tpsa: string;
    rotatable_bond: string;
    lipinski_failure: string;
    atcs: string;
};
// backend response datatype
type ChineseDrugsType = {
    related_ingredient: Related_ingredient[];
    chinese_name: { chinese_pharmaceutical_name: string }[];
    related_targets: {
        ingredients_id: string;
        protein: string;
        gene: string;
        chembl_id: string;
        uniprot_entry: string;
        kegg_id: string;
        pharmacological_action: string;
        druggable: string;
    }[];
    related_diseases: {
        id: string;
        level4: string;
        level4_description: string;
        targets: string;
    }[];
    related_pathways: {
        pathway_id: string;
        pathway_name: string;
        count_current_druggable_targets: number;
        p21: number;
        e21: number;
    }[];
    herb_basic_info: herbDetail;
    related_recipes: {
        recipes_id: string;
        chinese_character: string;
        pinyin: string;
        english_name: string;
    }[];
};

const PageChineseDrugs: React.FC = () => {
    const [result, setResult] = useState<ChineseDrugsType>();
    const [ipath3param, setIpath3Param] = useState<string[]>([]);
    const { name: searchId } = useParams<any>();
    const { get } = useFetch(API_SERVICE_BASE_URL);
    const history = useHistory();

    const goToTargetDetail = (keyword: string): void => {
        history.push(`/target/${keyword}`);
    };
    const goToDiseaseDetail = (keyword: string): void => {
        history.push(`/disease/detail/${keyword}`);
    };

    useEffect(() => {
        get('/api/related_plant_ingredients.php', { chinese_id: searchId }).then((r) => setResult(r));
        get('/api/ipath3_param.php', { chinese_id: searchId }).then((r) => setIpath3Param(ipath3ResponseProcessor(r)));
    }, []);
    return (
        <div>
            <Title level={3}>
                Herb (Processed Medicinal): {result?.chinese_name[0].chinese_pharmaceutical_name}(
                {result?.herb_basic_info.pinyin})
            </Title>
            <img
                src={
                    DATA_SERVICE_BASE_URL +
                    '/herb_pictures/' +
                    result?.chinese_name[0].chinese_pharmaceutical_name +
                    '.jpg'
                }
                alt="Herb Images"
                style={{ width: '400px', marginTop: '10px', marginBottom: '5px' }}
            />
            <br />
            <br />
            <Title level={4}>Chinese Medicinal Details</Title>
            <Descriptions bordered colon column={1} size="small" style={{ marginBottom: '3vh' }}>
                <Item label="Source">
                    {/* {result?.herb_basic_info.botanical_name} */}
                    <Link to={`/plant/detail/${result?.herb_basic_info.botanical_id}`} rel="noopener noreferrer">
                        {result?.herb_basic_info.botanical_name}
                    </Link>
                </Item>
                <Item label="Chinese Pharmaceutical Name">{result?.herb_basic_info.chinese_pharmaceutical_name}</Item>
                <Item label="Transliteration (Pinyin)">{result?.herb_basic_info.pinyin}</Item>
                <Item label="Pharmaceutical Latin Name">{result?.herb_basic_info.pharmaceutical_latin_name}</Item>
                <Item label="Used Part">{result?.herb_basic_info.used_part}</Item>
                <Item label="Chinese Pharmacopoeia">{result?.herb_basic_info.chp_2015}</Item>
            </Descriptions>

            <Title level={3}>KEGG Metabolic Pathways</Title>
            <Text>
                <li>green lines: biological targets</li>
                <li>blue lines: targets with pharmacological action</li>
                <li>red points: molecular ingredients</li>
                <li>violet points: ingredients with ATC code</li>
                Click image to view result in iPath3 interactively. The information about ingredients and targets
                information of this herb are stored in the element selection box on the iPath3 website. When you select
                any map in the iPath3 and submit our data, you can see the corresponding information displayed in the
                map.
            </Text>
            <br />
            <br />
            <form target="_blank" method="post" action="https://pathways.embl.de/ipath3.cgi">
                <button type="submit" style={{ cursor: 'pointer', border: 'none' }}>
                    <img
                        src={DATA_SERVICE_BASE_URL + `/results_2021_herb_ipath_img/${searchId}.svg`}
                        title="click image to view result in iPath3 interactively"
                        style={{ width: '100%' }}
                    />
                </button>
                <div style={{ opacity: 0 }}>
                    <textarea
                        name="selection"
                        value={ipath3param.reduce((acc, cur) => (acc === '' ? cur : acc + tAreaDivider + cur), '')}
                    ></textarea>
                    <input name="default_opacity" type="text" value="1"></input>
                    <input name="default_width" type="text" value="3"></input>
                    <input name="default_radius" type="text" value="7"></input>
                    <input name="keep_colors" type="checkbox" value="1"></input>
                    <input name="default_color" type="text" value="#aaaaaa"></input>
                    <input name="background_color" type="text" value="#ffffff"></input>
                    <input name="whole_pathways" type="checkbox" value="1"></input>
                    <input name="whole_modules" type="checkbox" value="1"></input>
                    <input name="query_reactions" type="checkbox" value="1"></input>
                    <input name="tax_filter" type="text" value=""></input>
                    <input name="map" type="radio" value="metabolic" checked></input>
                    <input name="map" type="radio" value="secondary"></input>
                    <input name="map" type="radio" value="microbial"></input>
                    <input name="map" type="radio" value="antibiotic"></input>
                    <input name="export_type" type="text" value="svg"></input>
                    <input name="export_dpi" type="text" value="120"></input>
                </div>
            </form>
            <Title level={4}>Related KEGG pathways</Title>
            <Table
                columns={columnsPathways}
                dataSource={result?.related_pathways}
                style={{ marginBottom: '3vh' }}
                // rowKey={(r): string => r.kegg_path_id}
            />

            <Title level={4}>
                Related Ingredients
                {/* {
                    <Button
                        style={{ float: 'right' }}
                        href={`${API_SERVICE_BASE_URL}/api/related_ingredients.php?chinese_id=${searchId}`}
                        target="_blank"
                        download
                    >
                        Download this Table
                    </Button>
                } */}
            </Title>
            <Table
                columns={columnRi}
                dataSource={result?.related_ingredient}
                style={{ marginBottom: '3vh' }}
                //rowKey={(r): string => r.ingredients_id}
            />
            <Title level={4}>
                Related Targets
                {/* {
                    <Button
                        style={{ float: 'right' }}
                        href={`${API_SERVICE_BASE_URL}/api/json_herb_targets.php?chinese_id=${searchId}`}
                        target="_blank"
                        download
                    >
                        Download this Table
                    </Button>
                } */}
            </Title>
            <Table
                columns={columnTargets}
                dataSource={result?.related_targets}
                style={{ marginBottom: '3vh' }}
                // rowKey={(r): string => r.uniprot_entry}
                // onRow={(record) => ({
                //     onClick: () => {
                //         goToTargetDetail(record.uniprot_entry);
                //     },
                //     style: { cursor: 'pointer' },
                // })}
            />
            <Title level={4}>Related Diseases</Title>
            <Table
                columns={columnDiseases}
                dataSource={result?.related_diseases}
                style={{ marginBottom: '3vh' }}
                // rowKey={(r): string => r.level4}
                // onRow={(record) => ({
                //     onClick: () => {
                //         goToDiseaseDetail(record.id);
                //     },
                //     style: { cursor: 'pointer' },
                // })}
            />
            <Title level={4}>Related Recipes</Title>
            <Table columns={columnRecipes} dataSource={result?.related_recipes} style={{ marginBottom: '3vh' }} />
        </div>
    );
};
export default PageChineseDrugs;
